import React, { useEffect } from 'react'
import { graphql, navigate } from 'gatsby'
import { mapAnimations, getBrowserLanguage, filterFragmentByTags } from '../components/utils'

import Layout from '../components/layout'
import Section from '../components/section/section'
import SectionNav from '../components/navigation/sectionNavigation'
import HelmetWrapper from '../components/helmet/helmetWrapper'

// Helper function to convert locale code to lowercase for URLs
const getLowercaseLocaleForUrl = (locale) => {
  return locale ? locale.toLowerCase() : locale;
}

const IndexPage = ({
  data,
  location
}) => {
  const {
    allContentfulLocale,
    allContentfulMetaHeader,
    allContentfulMetaFooter,
    contentfulPageLandingpage,
    allBegaEntryTags
  } = data

  let localeHeader = null
  let localeFooter = null
  let sectionNav = []

  // Determine the default locale
  let defaultLocale = { code: 'de', name: 'German' }; // Fallback

  if (allContentfulLocale && allContentfulLocale.edges && allContentfulLocale.edges.length > 0) {
    // Find the locale with default: true
    const defaultLocaleNode = allContentfulLocale.edges.find(edge => edge.node.default === true);
    if (defaultLocaleNode) {
      defaultLocale = {
        code: defaultLocaleNode.node.code,
        name: defaultLocaleNode.node.name
      };
    } else {
      // If no default locale is found, use the first one
      defaultLocale = {
        code: allContentfulLocale.edges[0].node.code,
        name: allContentfulLocale.edges[0].node.name
      };
    }
  }

  const langProps = {
    lang: allContentfulLocale && allContentfulLocale.edges ? allContentfulLocale.edges : [],
    // Fallback for activeLang if contentfulPageLandingpage doesn't exist
    activeLang: contentfulPageLandingpage ? contentfulPageLandingpage.node_locale : defaultLocale.code,
    // Use the determined default locale
    defaultLang: defaultLocale.code,
    isHomepage: true
  }

  // Filter available languages based on build mode
  const isPreviewMode = typeof window !== 'undefined' ?
    window.location.hostname.includes('preview') || window.location.hostname.includes('localhost') :
    typeof process !== 'undefined' && process.env.GATSBY_HOST === 'preview';

  // Filter languages for language switcher
  const filteredLangProps = {
    ...langProps,
    lang: langProps.lang.filter(item => {
      // In production builds (non-preview), exclude en-US
      if (!isPreviewMode && item.node.code === 'en-US') {
        return false;
      }
      return true;
    })
  };

  const browserLang = getBrowserLanguage(filteredLangProps)



  if (allContentfulMetaHeader && allContentfulMetaHeader.edges) {
    allContentfulMetaHeader.edges.forEach(metaHeader => {
      if (metaHeader.node.node_locale === langProps.activeLang) {
        localeHeader = metaHeader.node
      }
    })
  }

  if (allContentfulMetaFooter && allContentfulMetaFooter.edges) {
    allContentfulMetaFooter.edges.forEach(metaFooter => {
      if (metaFooter.node.node_locale === langProps.activeLang) {
        localeFooter = metaFooter.node
      }
    })
  }

  // Filter fragments for section navigation
  if (contentfulPageLandingpage && contentfulPageLandingpage.pagenavOption && contentfulPageLandingpage.fragments) {
    const filteredFragments = contentfulPageLandingpage.fragments
      .map(fragment => filterFragmentByTags(fragment, langProps.activeLang, allBegaEntryTags))
      .filter(Boolean);

    filteredFragments.forEach(fragment => {
      if (fragment.pageNavigation) {
        sectionNav.push({
          name: fragment.name,
          id: fragment.id
        })
      }
    })
  }

  useEffect(() => {
    const animatedEl = document.querySelectorAll('.js-gs-wrapper')

    if (animatedEl) {
      window.history.scrollRestoration = 'manual'
      window.onload = () => {
        if (window.scrollTo) window.scrollTo(0, 0);
      }

      mapAnimations()
    }
  }, [])

  if (browserLang && window.history.length <= 2) {
    navigate(
      `/${getLowercaseLocaleForUrl(browserLang)}`,
      { replace: false }
    )

    return null
  }

  return (
    <Layout header={localeHeader} lang={filteredLangProps} location={location} footer={localeFooter}>

      {contentfulPageLandingpage.seoJson &&
        <HelmetWrapper
          content={contentfulPageLandingpage.seoJson}
          activeLang={langProps.defaultLang}
        />
      }

      {contentfulPageLandingpage.fragments && (() => {
        const filteredFragments = contentfulPageLandingpage.fragments
          .map(fragment => filterFragmentByTags(fragment, langProps.activeLang, allBegaEntryTags))
          .filter(Boolean);

        return filteredFragments.map((section, index) =>
          <Section key={section.id} section={section} id={index} max={filteredFragments.length} />
        );
      })()}

      {contentfulPageLandingpage.pagenavOption &&
        <SectionNav items={sectionNav} />
      }

    </Layout>
  )
}

export default IndexPage

export const indexData = graphql`
  query HomepageQuery {
    site {
      siteMetadata {
        title
        description
      }
    }
    allContentfulLocale {
      totalCount
      edges {
        node {
          code
          name
          default
        }
      }
    }
    allContentfulMetaHeader(filter: {headerActive: {eq: "Yes"}}) {
      edges {
        node {
          ...metaHeaderFields
          navigation {
            __typename
            ... on ContentfulMetaLink {
              ...metaLinkFields
            }
            ... on ContentfulPageLandingpage {
              ...pageLandingpageFields
            }
          }
          contactFieldMobile {
            __typename
            ... on ContentfulMetaLink {
              ...metaLinkFields
            }
          }
        }
      }
    }
    contentfulPageLandingpage(homepageOption: { eq: "Yes" }) {
      ...pageLandingpageFields
      fragments {
        __typename
        ... on ContentfulFragmentSection {
          ...fragmentSectionFields
          fragments {
            __typename
            ... on ContentfulFragmentHeadline {
              ...fragmentHeadlineFields
            }
            ... on ContentfulFragmentRichText {
              ...fragmentRichTextFields
            }
            ... on ContentfulFragmentMedia {
              ...fragmentMediaFields
            }
            ... on ContentfulFragmentSlider {
              ...fragmentSliderFields
            }
            ... on ContentfulFragmentHtml {
              htmlCode {
                htmlCode
              }
            }
            ... on ContentfulFragmentGallery {
              ...fragmentGalleryFields
            }
          }
        }
      }
    }
    allContentfulMetaFooter(filter: {footerActive: {eq: "Yes"}}) {
      edges {
        node {
          id
          node_locale
          leftSection {
            __typename
            ... on ContentfulPageLandingpage {
              ...pageLandingpageFields
            }
            ... on ContentfulMetaLink {
              ...metaLinkFields
            }
          }
          centerSection {
            __typename
            ... on ContentfulPageLandingpage {
              ...pageLandingpageFields
            }
            ... on ContentfulMetaLink {
              ...metaLinkFields
            }
          }
          rightSection {
            __typename
            ... on ContentfulPageLandingpage {
              ...pageLandingpageFields
            }
            ... on ContentfulMetaLink {
              ...metaLinkFields
            }
          }
        }
      }
    }
    allBegaEntryTags {
      nodes {
        contentful_id
        contentType
        title
        locale
        tags {
          id
          name
          contentful_id
        }
      }
    }
  }
`
